import {
  Circle,
  ClipboardIcon,
  Flex,
  IconButton,
  Menu,
  Panel,
  PanelProps,
  RectangleStackIcon,
  Text,
} from 'components'
import { ReactNode } from 'react'
import { getDisplayDurationFromSeconds, useCopyHtml, useTheme } from 'utils'

interface Props extends PanelProps {
  /** (default RectangleStackIcon) */
  Icon?: (p: any) => ReactNode
  label: string
  num?: number
  children?: ReactNode
  labelOptions?: string[]
  onLabelSelect?: (label: string) => void
  canCopy?: boolean
}

export default function StatTile({
  Icon: _Icon,
  label,
  num,
  labelOptions,
  onLabelSelect,
  children,
  canCopy,
  ...rest
}: Props) {
  const { theme } = useTheme()
  const { contentRef, copyToClipboard } = useCopyHtml()
  const iconColor = theme === 'light' ? 'primary.500' : 'white'
  let value = num ? num.toLocaleString() : ''
  if (num && label.toLowerCase().includes('duration')) value = getDisplayDurationFromSeconds(num)

  const Icon = _Icon || RectangleStackIcon

  return (
    <Panel
      borderRadius="2xl"
      gap={2}
      p="15px"
      align="stretch"
      overflow="hidden"
      h={children ? '100%' : '85px'}
      minW="200px"
      position="relative"
      {...rest}
    >
      <Flex justify="space-between">
        <Flex direction="row" gap={4}>
          <Circle
            size={{ base: '40px', sm: '50px' }}
            bg={theme === 'light' ? 'white' : 'rgba(255, 255, 255, 0.20)'}
            boxShadow={'0px 4px 10px 0px rgba(0, 0, 0, 0.06)'}
            ml={{ base: '0', sm: '5' }}
          >
            <Icon fontSize="20px" color={iconColor} />
          </Circle>
          <Flex direction={'column'} gap={0} overflow="hidden" justify="center">
            {labelOptions ? (
              <Menu
                buttonLabel={label}
                size="xs"
                p={0}
                h="17px"
                menuProps={{ closeOnSelect: true }}
                value={label}
                onChange={onLabelSelect}
                options={labelOptions.map(value => ({ value }))}
              />
            ) : (
              <Text textStyle={value ? 'bodySmall' : 'bodyLarge'}>{label}</Text>
            )}
            <Text textStyle={{ base: 'h3', sm: 'h2' }}>{value}</Text>
          </Flex>
        </Flex>
        {canCopy && (
          <IconButton
            aria-label="Copy to clipboard"
            icon={<ClipboardIcon type="outline" />}
            onClick={copyToClipboard}
            borderRadius="full"
          />
        )}
      </Flex>
      <Flex flex={1} ref={contentRef}>
        {children}
      </Flex>
    </Panel>
  )
}
